// ─────────────────────────────────────────────────────────────────────────────
// DiscoveryMap.tsx — the Google Maps embed inside "Google Maps / local discovery".
//
// WHAT REPLACED WHAT. This panel used to be a ruled grid with the caption
// "no discovery pins yet — the board is live and fills as candidates are found".
// That is a caption where a map should be: it described the absence of PINS by
// withholding the MAP. Zero pins is a real map with nothing on it — pannable,
// zoomable, showing the continent the lanes are scoped to. The two are not the
// same thing and the caption was standing in for both.
//
// ── THE MAP MOVES LOCALLY ────────────────────────────────────────────────────
// Pan and zoom are the browser's. There is deliberately NO `idle` /
// `bounds_changed` / `zoom_changed` listener in this file and nothing fetches on
// view change: dragging the map is not an event in Rexy. A "search this area"
// control and a marker click ARE actions and would go to the box like every other
// button — NEITHER IS BUILT HERE, because neither has an endpoint to go to. A
// button that posts nowhere is worse than an absent one, and inventing the route
// would be writing an API contract, which is not this worker's to write.
//
// ── PRESENTATION ONLY ────────────────────────────────────────────────────────
// The pins are drawn from what the panel is already handed. This component reads
// no endpoint, writes nothing, and qualifies nothing — T1: "Maps presentation data
// does not itself equal verification/qualification."
//
// ── FOUR STATES, FOUR VOICES, NEVER SHARED ──────────────────────────────────
//   ready    — the map is drawn. Zero pins renders as a map with no pins.
//   no-key   — no browser key is configured. NOT an error and NOT "there is no
//              map": a named, actionable gap the owner closes by minting a key.
//   failed   — the script did not load. Transport, and it says so.
//   loading  — the script is in flight.
// A pin that carries no coordinates is a FIFTH thing and is counted out loud
// rather than silently dropped — see `unmappable` below.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MC } from './theme';
import {
  loadMaps, MAPS_BROWSER_KEY, MAP_DARK_STYLE, DEFAULT_VIEW, SINGLE_PIN_ZOOM,
  GMapsApi, GMapsMap, GMapsMarker,
} from './mapsProvider';

/** THE PIN CONTRACT — the shape this panel renders, stated once so the producer
 *  has something to write against. `lat`/`lng` are what makes a record MAPPABLE;
 *  everything else is label. A pin missing coordinates is still a real discovery,
 *  so it is counted and reported, never discarded to make the count look clean.
 *
 *  THE PRODUCER WRITES THIS SHAPE. server/growth_places.py::transform keeps
 *  `places.location` as NUMBERS — deliberately outside its string loop, and with
 *  bool excluded, since bool subclasses int and True would land as latitude 1 —
 *  and publishes through the PUT choke as board.marketing.maps_discovery.pins.
 *  A place returning no location is UNKNOWN on both axes, never 0/0, so an
 *  un-mappable pin arrives honestly and is counted rather than dropped.
 *
 *  (Written when the two backend halves were still open and the board answered
 *  {}. They landed in 2dba46dc; this note says what the code does now. The
 *  contract itself did not move — the producer was built to it.) */
export interface DiscoveryPin {
  id?: string;
  name?: string;
  address?: string;
  lat?: number;
  lng?: number;

  /** THE LANE, DERIVED FROM THE ADDRESS THE RECORD ALREADY CARRIES.
   *
   *  `server/growth_places.py::board_root_payload` reads it back off the
   *  record's own `scope_path` (`_lane_of`, segment 3) rather than tracking it
   *  separately — ONE derivation, so a pin can never disagree with the lane its
   *  record is filed under.
   *
   *  OPTIONAL BECAUSE THE PRODUCER EMITS `null` FOR A RECORD WITH NO
   *  COMPANY-SHAPED scope_path, and that is carried honestly: such a pin is
   *  still emitted. The worst case is a pin that cannot be filtered, never one
   *  that disappears off the map. */
  sector?: string;
}

type MapState = 'loading' | 'ready' | 'no-key' | 'failed';

function isFinitePair(p: DiscoveryPin): p is DiscoveryPin & { lat: number; lng: number } {
  return typeof p.lat === 'number' && isFinite(p.lat)
      && typeof p.lng === 'number' && isFinite(p.lng);
}

/** Accepts the raw `pins` value off the board without trusting its shape — the
 *  store is written elsewhere and a render must not throw on a surprise.
 *
 *  EXPORTED because Osiris reads the same projection off the same board. A
 *  second normaliser would be a second opinion about what a pin is — and it is
 *  the normaliser, not the interface, that decides whether `company_id` counts
 *  as an id and whether a non-numeric lat is dropped or coerced. */
export function readPins(raw: unknown): DiscoveryPin[] {
  if (!Array.isArray(raw)) return [];
  const out: DiscoveryPin[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const lat = typeof o.lat === 'number' ? o.lat : undefined;
    const lng = typeof o.lng === 'number' ? o.lng : undefined;
    out.push({
      id:      typeof o.id === 'string' ? o.id : (typeof o.company_id === 'string' ? o.company_id : undefined),
      name:    typeof o.name === 'string' ? o.name : undefined,
      address: typeof o.address === 'string' ? o.address
               : (typeof o.formatted_address === 'string' ? o.formatted_address : undefined),
      // The producer sends `null`, not an absent key, when a record has no
      // company-shaped scope_path. `typeof null` is 'object', so the same
      // guarded pattern every other field uses already lands it as undefined —
      // no null-specific branch, and no `null` leaking into a lane lookup.
      sector:  typeof o.sector === 'string' ? o.sector : undefined,
      lat, lng,
    });
  }
  return out;
}

export function DiscoveryMap({ pins: rawPins }: { pins: unknown }) {
  const host = useRef<HTMLDivElement | null>(null);
  const map = useRef<GMapsMap | null>(null);
  const markers = useRef<GMapsMarker[]>([]);
  const api = useRef<GMapsApi | null>(null);
  const [state, setState] = useState<MapState>(MAPS_BROWSER_KEY ? 'loading' : 'no-key');

  const pins = useMemo(() => readPins(rawPins), [rawPins]);
  const mappable = useMemo(() => pins.filter(isFinitePair), [pins]);
  const unmappable = pins.length - mappable.length;

  // ── LOAD + CONSTRUCT. Runs once; the map instance outlives every pin change. ──
  useEffect(() => {
    let live = true;
    if (!MAPS_BROWSER_KEY) { setState('no-key'); return; }

    loadMaps()
      .then((maps) => {
        if (!live || !host.current) return;
        api.current = maps;
        map.current = new maps.Map(host.current, {
          center: { lat: DEFAULT_VIEW.lat, lng: DEFAULT_VIEW.lng },
          zoom: DEFAULT_VIEW.zoom,
          styles: MAP_DARK_STYLE,
          // Chrome the operator does not need, and one control that changes the
          // view WITHOUT leaving the browser. No Street View pegman (it navigates
          // away from the board), no map-type switch (it would drop the dark style).
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: 'greedy',
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          keyboardShortcuts: false,
          backgroundColor: MC.panel,
          clickableIcons: false,
        });
        setState('ready');
      })
      .catch(() => { if (live) setState('failed'); });

    return () => { live = false; };
  }, []);

  // ── MARKERS. Rebuilt from the pins on every change; the old set is taken off
  //    the map first, so a pin that disappears from the board disappears here. ──
  useEffect(() => {
    const maps = api.current;
    const m = map.current;
    if (!maps || !m) return;

    for (const marker of markers.current) marker.setMap(null);
    markers.current = mappable.map((p) => new maps.Marker({
      position: { lat: p.lat, lng: p.lng },
      map: m,
      // The title is the browser's own tooltip — no click handler, because a click
      // is an action and has nowhere to go yet (see the header note).
      title: p.name || p.address || p.id || '',
    }));

    // FIT ONLY WHEN THERE IS SOMETHING TO FIT. With no pins the declared starting
    // view stands; fitting empty bounds would throw the map to an arbitrary corner.
    if (mappable.length === 1) {
      m.setCenter({ lat: mappable[0].lat, lng: mappable[0].lng });
      m.setZoom(SINGLE_PIN_ZOOM);
    } else if (mappable.length > 1) {
      const bounds = new maps.LatLngBounds();
      for (const p of mappable) bounds.extend({ lat: p.lat, lng: p.lng });
      m.fitBounds(bounds, 48);
    }
  }, [mappable, state]);

  return (
    <div>
      <div style={S.frame}>
        {/* The host div is mounted in EVERY state and never conditionally removed:
            the Maps API holds a reference to it, and swapping the node under a live
            map is how a map turns permanently grey. Overlays sit on top instead. */}
        <div ref={host} style={S.canvas} />
        {state !== 'ready' && (
          <div style={S.overlay}>
            {state === 'loading' && <span style={S.dim}>loading map…</span>}
            {state === 'no-key' && (
              <div style={S.gap}>
                <b style={S.gapHead}>browser key not configured</b>
                <span style={S.gapBody}>
                  The map draws as soon as a Maps JavaScript browser key is present.
                  It is a separate credential from the server Places key and is never
                  that one. Set <code style={S.code}>REACT_APP_GOOGLE_MAPS_BROWSER_KEY</code>
                  {' '}and rebuild — see <code style={S.code}>docs/GROWTH_MAPS_BROWSER_KEY.md</code>.
                </span>
              </div>
            )}
            {state === 'failed' && (
              <div style={S.gap}>
                <b style={{ ...S.gapHead, color: MC.red }}>map script did not load</b>
                <span style={S.gapBody}>
                  Transport or key rejection. The browser console carries the specific
                  Maps error code.
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      <div style={S.legend}>
        <span>{mappable.length === 0
          ? 'no pins on the board yet'
          : `${mappable.length} ${mappable.length === 1 ? 'pin' : 'pins'}`}</span>
        {unmappable > 0 && (
          <span style={{ color: MC.amber }}>
            {unmappable} discovered {unmappable === 1 ? 'company carries' : 'companies carry'} no
            {' '}coordinates and {unmappable === 1 ? 'is' : 'are'} not on the map
          </span>
        )}
      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  frame: {
    position: 'relative', height: 340, borderRadius: 16, overflow: 'hidden',
    border: `1px solid ${MC.line2}`, background: MC.panel3,
  },
  canvas: { position: 'absolute', inset: 0 },
  overlay: {
    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: '0 28px', textAlign: 'center',
    background: MC.panel3,
  },
  gap: { display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 460 },
  gapHead: { fontSize: 11.5, color: MC.amber, letterSpacing: 0.3 },
  gapBody: { fontSize: 10.5, lineHeight: 1.6, color: MC.muted },
  code: { fontSize: 10, color: MC.teal, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
  dim: { fontSize: 10.5, color: MC.dim },
  legend: {
    display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'space-between',
    fontSize: 10, color: MC.dim, padding: '8px 2px 0',
  },
};
